import {
  applicantSection,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * A9. Wniosek o wydanie zaświadczenia o niezaleganiu w podatkach lokalnych lub stwierdzającego stan zaległości —
 * art. 306a i 306e Ordynacji podatkowej (osoba trzecia za zgodą podatnika: art. 306h); opłata 21 zł od egzemplarza
 * (część II załącznika do ustawy o opłacie skarbowej). Brak wzoru ogólnopolskiego dla organów gminnych; układ jak wzory
 * Gminy Wągrowiec i m.st. Warszawy (dane, zakres podatków, cel przedłożenia, liczba egzemplarzy).
 */
const OTHER_TAXPAYER: EbiCondition = { field: "dotyczy", equals: "innyPodatnik" };
const WAIVED: EbiCondition = { field: "zwolnienie", equals: true };

const A9: IEbiFormDefinition = {
  code: "A9",
  version: 1,
  title: "Wniosek o wydanie zaświadczenia o niezaleganiu w podatkach lub stwierdzającego stan zaległości",
  category: EEbiFormCategory.Taxes,
  office: EEbiFormOffice.Municipality,
  description: "Zaświadczenie o niezaleganiu albo o stanie zaległości w podatkach i opłatach lokalnych.",
  keywords: ["zaświadczenie", "niezaleganie", "zaległości", "podatek od nieruchomości", "podatki lokalne"],
  organ: "Wójt / Burmistrz / Prezydent Miasta jako organ podatkowy",
  legalBasis: "art. 306a i art. 306e ustawy z dnia 29 sierpnia 1997 r. — Ordynacja podatkowa",
  request:
    "Proszę o wydanie zaświadczenia o niezaleganiu w podatkach lub stwierdzającego stan zaległości, do którego stosuje się przepisy art. 306e ustawy — Ordynacja podatkowa, w zakresie wskazanym poniżej.",
  feeLabel: "21 zł",
  fee: "21 zł za każdy egzemplarz zaświadczenia (chyba że cel jest zwolniony z opłaty).",
  deadlineLabel: "do 7 dni",
  deadline: "Bez zbędnej zwłoki, nie później niż w 7 dni (art. 306e § 2 Ordynacji podatkowej).",
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "podatnik",
      title: "Zaświadczenie dotyczy",
      fields: [
        {
          code: "dotyczy",
          type: "radio",
          label: "Zaświadczenie ma dotyczyć",
          required: true,
          defaultValue: "wnioskodawca",
          options: [
            { value: "wnioskodawca", label: "wnioskodawcy (mocodawcy, gdy działa pełnomocnik)" },
            { value: "innyPodatnik", label: "innego podatnika — za jego zgodą (art. 306h Ordynacji podatkowej)" },
          ],
        },
        { code: "podatnikNazwa", type: "text", label: "Imię i nazwisko albo nazwa podatnika", required: true, visibleWhen: OTHER_TAXPAYER, maxLength: 300 },
        { code: "podatnikIdentyfikator", type: "text", label: "PESEL albo NIP podatnika", required: true, visibleWhen: OTHER_TAXPAYER, width: "half", maxLength: 20 },
        { code: "podatnikAdres", type: "text", label: "Adres podatnika", required: true, visibleWhen: OTHER_TAXPAYER, maxLength: 300 },
      ],
    },
    {
      code: "zakres",
      title: "Zakres zaświadczenia",
      fields: [
        {
          code: "rodzaj",
          type: "radio",
          label: "Rodzaj zaświadczenia",
          required: true,
          defaultValue: "niezaleganie",
          options: [
            { value: "niezaleganie", label: "o niezaleganiu w podatkach" },
            { value: "stanZaleglosci", label: "stwierdzające stan zaległości" },
          ],
        },
        {
          code: "podatki",
          type: "checkboxes",
          label: "Podatki i opłaty",
          required: true,
          defaultValue: ["wszystkie"],
          options: [
            { value: "wszystkie", label: "wszystkie podatki i opłaty lokalne" },
            { value: "nieruchomosci", label: "podatek od nieruchomości" },
            { value: "rolny", label: "podatek rolny" },
            { value: "lesny", label: "podatek leśny" },
            { value: "transport", label: "podatek od środków transportowych" },
            { value: "odpady", label: "opłata za gospodarowanie odpadami komunalnymi" },
          ],
        },
        {
          code: "cel",
          type: "textarea",
          label: "Celem przedłożenia w",
          required: true,
          maxLength: 500,
          hint: "Instytucja i cel, np. „bank — wniosek o kredyt”, „przetarg nieograniczony w Urzędzie Miasta”.",
        },
        { code: "egzemplarze", type: "number", label: "Liczba egzemplarzy", required: true, min: 1, max: 10, defaultValue: "1", width: "third" },
      ],
    },
    {
      code: "oplata",
      title: "Opłata skarbowa",
      fields: [
        {
          code: "zwolnienie",
          type: "checkbox",
          label: "Zaświadczenie jest zwolnione z opłaty skarbowej (np. w sprawach alimentacyjnych, świadczeń socjalnych — art. 2 ustawy o opłacie skarbowej)",
        },
        { code: "podstawaZwolnienia", type: "text", label: "Podstawa zwolnienia", required: true, visibleWhen: WAIVED, maxLength: 300 },
        {
          code: "kwotaOplaty",
          type: "fee",
          label: "Opłata skarbowa do wniesienia",
          feeAmount: 21,
          feeUnitField: "egzemplarze",
          feeWaivedWhen: WAIVED,
          hint: "21 zł za każdy egzemplarz — dołącz potwierdzenie przelewu na rachunek urzędu.",
        },
      ],
    },
    replySection("Sposób odbioru zaświadczenia"),
  ],
  attachments: [
    {
      code: "oplata",
      label: "Dowód uiszczenia opłaty skarbowej",
      visibleWhen: { not: WAIVED },
      required: true,
    },
    {
      code: "zgodaPodatnika",
      label: "Zgoda podatnika na wydanie zaświadczenia (art. 306h Ordynacji podatkowej)",
      visibleWhen: OTHER_TAXPAYER,
      required: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default A9;
